import { useState, useEffect } from "react";
import {
  Grid,
  Button,
  Slider,
  Typography,
  Tooltip,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import VoiceSelector from "@nualang/nualang-ui-components/dist/Forms/VoiceSelector";
import LanguageSelector from "@nualang/nualang-ui-components/dist/Forms/LanguageSelector/LanguageSelector";
import AvatarSelector from "@nualang/nualang-ui-components/dist/Misc/AvatarSelector/AvatarSelector";
import InputHelper from "@nualang/nualang-ui-components/dist/Forms/InputHelper";
import { containsInvalidSymbols } from "@nualang/nualang-ui-components/dist/utils";
import AudioNuala from "@nualang/nualang-ui-components/dist/img/AudioNuala.svg";
import Papa from "papaparse";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ResponsiveDialog from "@nualang/nualang-ui-components/dist/Dialogs/ResponsiveDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import SpeedIcon from "@mui/icons-material/Speed";
import Checkbox from "@mui/material/Checkbox";
import logger from "@nualang/nualang-ui-components/dist/logger";

function VoiceSpeedSlider({
  t,
  selectedVoice,
  voiceSpeed,
  onVoiceSpeedChange,
  handleSpeak,
  actor,
  learnLang,
}) {
  const voiceSpeedMarks = [
    {
      value: 5,
      label: t("voice_speed_extra_slow"),
      speedValue: "x-slow",
    },
    {
      value: 25,
      label: t("voice_speed_slow"),
      speedValue: "slow",
    },
    {
      value: 50,
      label: t("voice_speed_medium"),
      speedValue: null,
    },
    {
      value: 75,
      label: t("voice_speed_fast"),
      speedValue: "fast",
    },
    {
      value: 95,
      label: t("voice_speed_extra_fast"),
      speedValue: "x-fast",
    },
  ];

  const sliderValue =
    voiceSpeedMarks.find((mark) => mark.speedValue === voiceSpeed)?.value || 50;

  if (selectedVoice?.isNeuralOnly) {
    return (
      <Tooltip title={t("voice_speed_disabled")}>
        <div>
          <Slider
            disabled={true}
            aria-label={t("voice_speed")}
            defaultValue={50}
            step={null}
            marks={voiceSpeedMarks}
            value={sliderValue}
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <Slider
      aria-label={t("voice_speed")}
      defaultValue={50}
      step={null}
      marks={voiceSpeedMarks}
      value={sliderValue}
      onChange={(_, newValue) => {
        const newSpeedValue = voiceSpeedMarks.find(
          (mark) => mark.value === newValue,
        )?.speedValue;
        onVoiceSpeedChange({
          target: {
            name: "voiceSpeed",
            value: newSpeedValue,
          },
        });
        handleSpeak({
          text: actor.voice,
          learnLanguage: learnLang,
          voiceName: actor.voice,
          languageCode: actor.voiceLanguageCode,
          pitch: actor.voicePitch,
          voiceSpeed: newSpeedValue,
        });
      }}
    />
  );
}

function ActorForm({
  t,
  actor,
  avatars,
  handleBlur,
  touched,
  errors,
  learnLang,
  handleSpeak,
  filteredVoices,
  setFieldValue,
  setCheckAudioUrl = () => {},
  formik,
  csv = false,
}) {
  const [avatarSelector, setAvatarSelector] = useState(false);

  const handleAvatarSelection = (value) => {
    setFieldValue("actor.picture", value);
    setAvatarSelector(false);
  };

  const handleVoiceChange = (e) => {
    const voiceSelected = e.target.value;
    const languageCode = e.target.languageCode;

    setFieldValue("actor.voice", voiceSelected);
    setFieldValue("actor.voiceLanguageCode", languageCode);
    setFieldValue("actor.voicePitch", null);
    setFieldValue("voiceSpeed", "");

    handleSpeak({
      text: voiceSelected,
      learnLanguage: learnLang,
      voiceName: voiceSelected,
      languageCode,
    });
    setCheckAudioUrl(true);
  };

  const handleVoicePitchChange = (e) => {
    const pitch = e.target.value;

    setFieldValue("actor.voicePitch", pitch);

    handleSpeak({
      text: actor.voice,
      learnLanguage: learnLang,
      voiceName: actor.voice,
      languageCode: actor.voiceLanguageCode,
      pitch,
      voiceSpeed: formik.values.voiceSpeed,
    });
    setCheckAudioUrl(true);
  };

  return (
    <>
      {learnLang !== "irish" && (
        <VoiceSelector
          handleSpeak={handleSpeak}
          learnLang={learnLang}
          id={actor.voice}
          name='actor.voice'
          label={t("actor_voice")}
          margin={"normal"}
          variant={"outlined"}
          fullWidth
          required={learnLang !== "irish"}
          t={t}
          voices={filteredVoices}
          value={actor.voice}
          valueLanguageCode={actor.voiceLanguageCode}
          onChange={handleVoiceChange}
          onBlur={handleBlur}
          helperText={touched?.actor?.voice && errors?.actor?.voice}
          error={touched?.actor?.voice && Boolean(errors?.actor?.voice)}
          voicePitch={actor.voicePitch}
          onVoicePitchChange={handleVoicePitchChange}
          csv={csv}
          generateAudio={true}
        />
      )}
      <AvatarSelector
        t={t}
        open={avatarSelector}
        avatars={avatars}
        picture={actor.picture}
        handleClose={() => setAvatarSelector(false)}
        handleSubmit={handleAvatarSelection}
      />
      {formik.values.actor.voice && (
        <>
          <Box sx={{ width: "100%", px: 1, my: 2 }}>
            <Typography variant='body1' sx={{ marginTop: 2 }}>
              {t("voice_speed")}
            </Typography>
            <VoiceSpeedSlider
              t={t}
              selectedVoice={formik.values.actor.voice}
              voiceSpeed={formik.values.voiceSpeed}
              onVoiceSpeedChange={(event) => {
                formik.setFieldValue("voiceSpeed", event.target.value);
              }}
              handleSpeak={handleSpeak}
              actor={formik.values.actor}
              learnLang={learnLang}
            />
          </Box>
        </>
      )}
    </>
  );
}

// The grid stores a voice as a bare name, sometimes wrapped in the Box that
// renders its pitch and speed chips.
function getVoiceName(voice) {
  if (voice && typeof voice === "object") {
    return voice.props.children[0];
  }
  return voice;
}

// A voice name can appear under more than one language ("Mia" is Polly on es-MX
// and Azure on en-GB), so the language being generated for is searched first.
// The global search is the fallback for a voice borrowed from another language.
function findVoiceLanguage(voice, languages, preferredLanguage) {
  const finalVoice = getVoiceName(voice);
  if (!finalVoice) return null;
  const orderedLanguages = Object.keys(languages || {});
  if (preferredLanguage && languages?.[preferredLanguage]) {
    orderedLanguages.unshift(preferredLanguage);
  }
  for (const language of orderedLanguages) {
    const voicesObject = languages[language].Voices;
    if (!voicesObject) continue;
    for (const langKey in voicesObject) {
      const voiceOptions = voicesObject[langKey].voiceOptions;
      if (voiceOptions?.find((option) => option.name === finalVoice)) {
        return { language, languageCode: langKey };
      }
    }
  }
  return null;
}

function getVoiceLanguageCode(voice, languages, preferredLanguage) {
  const match = findVoiceLanguage(voice, languages, preferredLanguage);
  return match ? match.languageCode : null;
}


function AddPhraseDialog({
  t,
  open,
  onClose,
  handleAddPhrase,
  languages,
  avatars,
  handleSpeak,
  langChar,
}) {
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [learnLang, setLearnLang] = useState("english");
  const formik = useFormik({
    initialValues: {
      learnLang: "english",
      actor: {
        voice: "",
        voiceLanguageCode: "",
        voicePitch: "",
        picture: "",
      },
      phraseToSpeak: "",
      voiceSpeed: "",
      fileName: "",
    },
    validationSchema: Yup.object({
      actor: Yup.object().shape({
        voice: Yup.string().nullable(),
        voiceLanguageCode: Yup.string().nullable(),
        voicePitch: Yup.string().nullable(),
        picture: Yup.string().nullable(),
      }),
      phraseToSpeak: Yup.string().required("Required"),
      fileName: Yup.string(),
    }),
    onSubmit: (values) => {
      const phraseToAdd = {
        phrase: values.phraseToSpeak,
        actor: values.actor,
        fileName: values.fileName,
        voiceSpeed: values.voiceSpeed,
      };
      handleAddPhrase(phraseToAdd);
      formik.resetForm();
      onClose();
    },
  });

  useEffect(() => {
    if (learnLang) {
      const voicesForLang = languages[learnLang]?.Voices || [];
      setFilteredVoices(voicesForLang);
    }
  }, [learnLang, languages]);

  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    setLearnLang(selectedLang);
    formik.setFieldValue("actor.voice", "");
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth='80%'>
      <DialogTitle>{t("add_phrase")}</DialogTitle>
      <DialogContent>
        <Grid container sx={{ width: "100%" }} spacing={2}>
          <Grid size={12} sx={{
            marginTop: 2
          }}>
            <LanguageSelector
              id='learnLang'
              name='learnLang'
              label={t("language")}
              fullWidth
              required
              t={t}
              languages={languages}
              value={learnLang}
              onChange={handleLanguageChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid size={12}>
            <ActorForm
              t={t}
              actor={formik.values.actor}
              handleBlur={formik.handleBlur}
              touched={formik.touched}
              errors={formik.errors}
              learnLang={learnLang}
              filteredVoices={filteredVoices}
              handleSpeak={handleSpeak}
              avatars={avatars}
              langChar={langChar}
              setFieldValue={formik.setFieldValue}
              formik={formik}
              csv={true}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id='fileName'
              data-cy='fileName'
              name='fileName'
              label={t("file_name")}
              value={formik.values.fileName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type='text'
              margin='normal'
              variant='outlined'
              fullWidth
              helperText={formik.touched.fileName && formik.errors.fileName}
              error={formik.touched.fileName && Boolean(formik.errors.fileName)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>.mp3</InputAdornment>
                  ),
                }
              }}
            />
          </Grid>
          <Grid size={12} sx={{
            marginTop: -1
          }}>
            <InputHelper
              t={t}
              id='phraseToSpeak'
              data-cy='phraseToSpeak'
              name='phraseToSpeak'
              label={t("phrase_to_speak")}
              value={formik.values.phraseToSpeak}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type='text'
              margin='normal'
              variant='outlined'
              fullWidth
              required
              multiline
              characters={langChar}
              learnLang={learnLang}
              helperText={
                formik.touched.phraseToSpeak && formik.errors.phraseToSpeak
              }
              error={
                formik.touched.phraseToSpeak &&
                Boolean(formik.errors.phraseToSpeak)
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
        <Button
          variant='outlined'
          color='primary'
          disabled={
            formik.values.phraseToSpeak === "" || !formik.values.actor.voice
          }
          onClick={() =>
            handleSpeak({
              text: formik.values.phraseToSpeak,
              learnLanguage: learnLang,
              voiceName: formik.values.actor.voice,
              mute: false,
              callback: () => {},
              textContainer: null,
              languageCode: formik.values.actor.voiceLanguageCode,
              isHoverText: false,
              pitch: formik.values.actor.voicePitch,
              voiceSpeed: formik.values.voiceSpeed,
            })
          }
        >
          {t("play_audio")}
        </Button>
        <Button
          variant='contained'
          onClick={formik.handleSubmit}
          color='primary'
          disabled={
            formik.values.phraseToSpeak === "" || !formik.values.actor.voice
          }
        >
          {t("add_phrase")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

function EditPhraseDialog({
  t,
  open,
  onClose,
  handleUpdatePhrase,
  languages,
  learnLang,
  avatars,
  handleSpeak,
  langChar,
  selectedRow,
  rowIndex,
}) {
  const [selectedLanguage, setSelectedLanguage] = useState("english");
  const [selectedLanguageCode, setSelectedLanguageCode] = useState("en-US");
  const [filteredVoices, setFilteredVoices] = useState([]);

  let finalVoice, finalPitch, finalSpeed;
  const rowVoice = selectedRow?.voice;
  if (rowVoice && typeof rowVoice === "object") {
    finalVoice = rowVoice.props.children[0];
    finalPitch = rowVoice.props.children[1]?.props?.title;
    finalSpeed = rowVoice.props.children[2]?.props?.title;
  } else {
    finalVoice = rowVoice;
  }

  const formik = useFormik({
    initialValues: {
      learnLang: selectedLanguage || "english",
      actor: {
        voice: finalVoice || "",
        voiceLanguageCode: selectedLanguageCode || "",
        voicePitch: finalPitch || null,
        picture: null,
      },
      phraseToSpeak: selectedRow?.phrase || "",
      voiceSpeed: finalSpeed || "",
      fileName: selectedRow?.fileName || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      actor: Yup.object().shape({
        voice: Yup.string().nullable(),
        voiceLanguageCode: Yup.string().nullable(),
        voicePitch: Yup.string().nullable(),
        picture: Yup.string().nullable(),
      }),
      phraseToSpeak: Yup.string().required("Required"),
      fileName: Yup.string(),
    }),
    onSubmit: (values) => {
      if (
        !values.phraseToSpeak.startsWith('"') &&
        !values.phraseToSpeak.endsWith('"')
      ) {
        values.phraseToSpeak = `"${values.phraseToSpeak}"`;
      }
      let phraseVoice = values.actor.voice;
      const phrasePitch = values.actor.voicePitch;
      const phraseSpeed = values.voiceSpeed;
      phraseVoice = (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
          {phraseVoice}
          {phrasePitch && (
            <Tooltip title={phrasePitch} placement='top'>
              <RecordVoiceOverIcon
                sx={{
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "#ffffff" : "#646464",
                }}
              />
            </Tooltip>
          )}
          {phraseSpeed !== "" && (
            <Tooltip title={phraseSpeed} placement='top'>
              <SpeedIcon
                sx={{
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "#ffffff" : "#646464",
                }}
              />
            </Tooltip>
          )}
        </Box>
      );

      const updatedRow = {
        ...selectedRow,
        phrase: values.phraseToSpeak,
        voice: phraseVoice,
        voiceLanguageCode: values.actor.voiceLanguageCode || null,
        fileName: values.fileName,
      };
      handleUpdatePhrase(updatedRow, rowIndex);
      onClose();
    },
  });

  useEffect(() => {
    if (selectedRow?.voice) {
      const match = findVoiceLanguage(selectedRow.voice, languages, learnLang);
      setSelectedLanguage(match?.language ?? null);
      // An uploaded row has no accent of its own, so its name is all there is.
      setSelectedLanguageCode(
        selectedRow.voiceLanguageCode ?? match?.languageCode ?? null,
      );
    }
  }, [selectedRow, languages, learnLang]);

  useEffect(() => {
    if (selectedLanguage) {
      const voicesForLang = languages[selectedLanguage]?.Voices || [];
      setFilteredVoices(voicesForLang);
    }
  }, [selectedLanguage, languages]);

  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    setSelectedLanguage(selectedLang);
    formik.setFieldValue("actor.voice", "");
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth='80%'>
      <DialogTitle>{t("edit_phrase")}</DialogTitle>
      <DialogContent>
        <Grid container sx={{ width: "100%" }} spacing={2}>
          <Grid size={12} sx={{
            marginTop: 2
          }}>
            <LanguageSelector
              id='learnLang'
              name='learnLang'
              label={t("language")}
              fullWidth
              required
              t={t}
              languages={languages}
              value={selectedLanguage}
              onChange={handleLanguageChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid size={12}>
            <ActorForm
              t={t}
              actor={formik.values.actor}
              handleBlur={formik.handleBlur}
              touched={formik.touched}
              errors={formik.errors}
              learnLang={selectedLanguage}
              filteredVoices={filteredVoices}
              handleSpeak={handleSpeak}
              avatars={avatars}
              langChar={langChar}
              setFieldValue={formik.setFieldValue}
              formik={formik}
              csv={true}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id='fileName'
              name='fileName'
              label={t("file_name")}
              value={formik.values.fileName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type='text'
              margin='normal'
              variant='outlined'
              fullWidth
              helperText={formik.touched.fileName && formik.errors.fileName}
              error={formik.touched.fileName && Boolean(formik.errors.fileName)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>.mp3</InputAdornment>
                  ),
                }
              }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id='phraseToSpeak'
              name='phraseToSpeak'
              label={t("phrase_to_speak")}
              value={formik.values.phraseToSpeak}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type='text'
              margin='normal'
              variant='outlined'
              fullWidth
              required
              multiline
              characters={langChar}
              helperText={
                formik.touched.phraseToSpeak && formik.errors.phraseToSpeak
              }
              error={
                formik.touched.phraseToSpeak &&
                Boolean(formik.errors.phraseToSpeak)
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
        <Button
          variant='outlined'
          color='primary'
          disabled={
            formik.values.phraseToSpeak === "" || !formik.values.actor.voice
          }
          onClick={() => {
            handleSpeak({
              text: formik.values.phraseToSpeak,
              learnLanguage: selectedLanguage,
              voiceName: formik.values.actor.voice,
              mute: false,
              callback: () => {},
              textContainer: null,
              languageCode: formik.values.actor.voiceLanguageCode,
              isHoverText: false,
              pitch: formik.values.actor.voicePitch,
              voiceSpeed: formik.values.voiceSpeed,
            });
          }}
        >
          {t("play_audio")}
        </Button>
        <Button
          variant='contained'
          onClick={formik.handleSubmit}
          color='primary'
        >
          {t("update_phrase")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

function GenerateAudio({
  t,
  handleSpeak,
  avatars,
  languages,
  langChar,
  downloadAudio,
  audioUrl,
  openSnackbar,
  generateTxt,
  setGenerateTxt,
}) {
  const [learnLang, setLearnLang] = useState("english");
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [checkAudioUrl, setCheckAudioUrl] = useState(false);
  const [voiceSpeed, setVoiceSpeed] = useState(50);
  const [showCSV, setShowCSV] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState(["phrase", "fileName", "voice"]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowIndex, setRowIndex] = useState(null);

  const openEditDialog = (row, index) => {
    setSelectedRow(row);
    setRowIndex(index);
    setEditDialogOpen(true);
  };

  const formik = useFormik({
    initialValues: {
      actor: {
        name: "",
        voice: "",
        voiceLanguageCode: "",
        voicePitch: "",
        picture: "",
      },
      phraseToSpeak: "",
      voiceSpeed: "",
    },
    validationSchema: Yup.object({
      actor: Yup.object().shape({
        name: Yup.string("")
          .required("Required")
          .test(
            "noSpecialChars",
            "No special characters",
            (value) => !containsInvalidSymbols(value),
          ),
        voice: Yup.string().nullable(),
        voiceLanguageCode: Yup.string().nullable(),
        voicePitch: Yup.string().nullable(),
        picture: Yup.string().nullable(),
      }),
      phraseToSpeak: Yup.string()
        .required("Required")
        .test(
          "noSpecialChars",
          "No special characters",
          (value) => !containsInvalidSymbols(value),
        ),
    }),
    onSubmit: (values) => {
      handleSpeak(values.phraseToSpeak, values.actor.voice);
    },
  });

  useEffect(() => {
    setCheckAudioUrl(true);
  }, [formik.values.phraseToSpeak]);

  useEffect(() => {
    if (learnLang) {
      const voicesForLang = languages[learnLang]?.Voices || [];
      setFilteredVoices(voicesForLang);
    }
  }, [learnLang, languages]);

  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    setLearnLang(selectedLang);
    formik.setFieldValue("actor.voice", "");
    setCheckAudioUrl(true);
  };

  const handleSpeedChange = (event, newValue) => {
    setVoiceSpeed(newValue);
  };

  const playPhraseAudio = (phrase, rowVoice, rowLanguageCode) => {
    let finalVoice, finalPitch, finalSpeed;
    if (rowVoice && typeof rowVoice === "object") {
      finalVoice = rowVoice.props.children[0];
      finalPitch = rowVoice.props.children[1]?.props?.title;
      finalSpeed = rowVoice.props.children[2]?.props?.title;
    } else {
      finalVoice = rowVoice;
    }
    const voiceToUse = finalVoice || formik.values.actor.voice || "Joey";

    handleSpeak({
      text: phrase,
      learnLanguage: formik.values.learnLang || "english",
      voiceName: voiceToUse,
      mute: false,
      callback: () => {},
      textContainer: null,
      languageCode:
        rowLanguageCode ||
        getVoiceLanguageCode(
          voiceToUse,
          languages,
          formik.values.learnLang || "english",
        ),
      isHoverText: false,
      pitch: finalPitch || null,
      voiceSpeed: finalSpeed || "",
    });
  };

  const languagesContainsVoice = (voice) => {
    for (const languageCode in languages) {
      const voicesObject = languages[languageCode].Voices;
      if (voicesObject) {
        for (const langKey in voicesObject) {
          const voiceOptions = voicesObject[langKey].voiceOptions;
          if (voiceOptions?.find((option) => option.name === voice)) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const handleAddPhrase = (newPhrase) => {
    csvHeaders.length === 0 && setCsvHeaders(["phrase", "fileName", "voice"]);
    const fileName = `audio_${csvData.length + 1}_${new Date().toISOString().replace(/:/g, "-").slice(0, 19)}.mp3`;
    if (newPhrase.fileName && !newPhrase.fileName.endsWith(".mp3")) {
      newPhrase.fileName = `${newPhrase.fileName}.mp3`;
    }
    if (!newPhrase.phrase.startsWith('"') && !newPhrase.phrase.endsWith('"')) {
      newPhrase.phrase = `"${newPhrase.phrase}"`;
    }

    let phraseVoice = newPhrase.actor.voice || "Joey";
    const phrasePitch = newPhrase.actor.voicePitch;
    phraseVoice = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
        {phraseVoice}
        {phrasePitch && (
          <Tooltip title={phrasePitch} placement='top'>
            <RecordVoiceOverIcon
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#ffffff" : "#646464",
              }}
            />
          </Tooltip>
        )}
        {newPhrase.voiceSpeed !== "" && (
          <Tooltip title={newPhrase.voiceSpeed} placement='top'>
            <SpeedIcon
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#ffffff" : "#646464",
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
    const phraseWithDefaults = {
      phrase: newPhrase.phrase,
      fileName: newPhrase.fileName || fileName,
      voice: phraseVoice,
      // The row renders the voice as a Box, so the name alone survives in it.
      // Two accents can share a name, so the chosen one is kept alongside.
      voiceLanguageCode: newPhrase.actor.voiceLanguageCode || null,
    };
    setCsvData([...csvData, phraseWithDefaults]);
  };

  const handleUpdatePhrase = (updatedRow, rowIndex) => {
    if (
      !updatedRow.phrase.startsWith('"') &&
      !updatedRow.phrase.endsWith('"')
    ) {
      updatedRow.phrase = `"${updatedRow.phrase}"`;
    }
    const updatedCSVData = [...csvData];
    updatedCSVData[rowIndex] = updatedRow;
    setCsvData(updatedCSVData);
  };

  const downloadCSVTemplate = () => {
    const csvTemplate = [
      "phrase,fileName,voice,voiceSpeed,voicePitch",
      "Normal speed & pitch,Test.mp3,Joey,,",
      "X-High & X-Fast,Test2.mp3,Joey,x-fast,x-high",
      "X-Low & X-Slow,Test3.mp3,Joey,x-slow,x-low",
      "Fast & High,Test4.mp3,Joey,fast,high",
      "Slow & Low,Test5.mp3,Joey,slow,low",
    ].join("\n");
    downloadCSV(csvTemplate, "template.csv");
  };

  const handleCSVUpload = (event) => {
    const file = event.target.files[0];

    if (file) {
      const reader = new FileReader();

      reader.onload = function (e) {
        try {
          new TextDecoder("utf-8", { fatal: true }).decode(e.target.result);
          Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function (results) {
              let headers = results.meta.fields || [];

              // Ensure 'phrase' is present in the headers
              if (headers.includes("phrase")) {
                if (!headers.includes("fileName")) {
                  headers.push("fileName");
                }
                if (!headers.includes("voice")) {
                  headers.push("voice");
                }

                const data = results.data
                  .map((row, index) => {
                    if (Object.keys(row).length !== headers.length) {
                      openSnackbar(
                        `Row ${index + 1} has been skipped. Please wrap the phrase with a comma in double quotes before uploading.`,
                        "warning",
                      );
                      return null;
                    }
                    if (!row.phrase) {
                      openSnackbar(
                        `Row ${index + 1} is missing a phrase, it has been skipped.`,
                        "warning",
                      );
                      return null; // Invalid row, will be filtered out
                    } else {
                      row.phrase = `"${row.phrase}"`;
                    }

                    if (!row.fileName) {
                      row.fileName = `audio_${index + 1}_${new Date().toISOString().replace(/:/g, "-").slice(0, 19)}.mp3`;
                    } else {
                      row.fileName = row.fileName.endsWith(".mp3")
                        ? row.fileName
                        : `${row.fileName}.mp3`;
                    }
                    row.voice = languagesContainsVoice(row.voice)
                      ? row.voice
                      : "Joey";
                    let voiceSpeed = "";
                    let pitch = null;
                    const validSpeeds = ["x-slow", "slow", "fast", "x-fast"];
                    const validPitches = ["x-low", "low", "high", "x-high"];
                    if (
                      row.voiceSpeed &&
                      validSpeeds.includes(row.voiceSpeed)
                    ) {
                      voiceSpeed = row.voiceSpeed;
                    }
                    if (
                      row.voicePitch &&
                      validPitches.includes(row.voicePitch)
                    ) {
                      pitch = row.voicePitch;
                    }

                    row.voice = (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1
                        }}>
                        {row.voice}
                        {pitch && (
                          <Tooltip title={pitch} placement='top'>
                            <RecordVoiceOverIcon
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#646464",
                              }}
                            />
                          </Tooltip>
                        )}
                        {voiceSpeed !== "" && (
                          <Tooltip title={voiceSpeed} placement='top'>
                            <SpeedIcon
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#646464",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    );

                    return row;
                  })
                  .filter(Boolean);

                setCsvData(csvData.length > 0 ? [...csvData, ...data] : data);
                setCsvHeaders(csvHeaders.length > 0 ? csvHeaders : headers);
              } else {
                openSnackbar("CSV must contain 'phrase' header.", "error");
                removeCSVFile();
              }
            },
            error: function (error) {
              console.error("Error parsing CSV: ", error);
            },
          });
        } catch (error) {
          logger.error(`Error parsing CSV: ${error}`);
          openSnackbar(
            "The uploaded file must be UTF-8 encoded. Please check your file encoding.",
            "error",
          );
        }
      };

      reader.readAsArrayBuffer(file);
    }
  };

  const removeCSVFile = () => {
    setCsvData([]);
    setCsvHeaders([]);
  };

  const handleDeleteRow = (rowIndex) => {
    const updatedCsvData = csvData.filter((_, index) => index !== rowIndex);
    setCsvData(updatedCsvData);
  };

  const handleChange = (event, newValue) => {
    setShowCSV(newValue);
    setGenerateTxt(false);
  };

  const downloadCSV = (text, filename) => {
    const element = document.createElement("a");
    const file = new Blob([text], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  const generateAudioForCSV = async () => {
    const zip = new JSZip();
    const timestamp = new Date().toISOString().replace(/:/g, "-").slice(0, 19);
    const folder = zip.folder(`audio_files_${timestamp}`);
    setIsGenerating(true);
    const fileNameCount = {};

    let newData = JSON.parse(JSON.stringify(csvData));
    let newHeaders = [...csvHeaders];

    try {
      for (const row of newData) {
        if (!row.phrase.startsWith('"') && !row.phrase.endsWith('"')) {
          row.phrase = `"${row.phrase}"`;
        }
        const phrase = row.phrase;
        let fileName =
          row.fileName ||
          `audio_${new Date().toISOString().replace(/:/g, "-").slice(0, 19)}`;
        const rowVoice = row.voice || formik.values.actor.voice;

        let finalRowVoice = rowVoice;
        let finalRowPitch = null;
        let finalRowSpeed = "";
        if (typeof rowVoice === "object") {
          if (!newHeaders.includes("voiceSpeed")) {
            newHeaders.push("voiceSpeed");
          }
          if (!newHeaders.includes("voicePitch")) {
            newHeaders.push("voicePitch");
          }
          finalRowVoice = rowVoice.props.children[0];
          finalRowPitch = rowVoice.props.children[1]?.props?.title;
          finalRowSpeed = rowVoice.props.children[2]?.props?.title;
        }
        if (!phrase) {
          openSnackbar(
            "Invalid CSV data: 'phrase' column is required",
            "error",
          );
          continue;
        }

        row.voice = finalRowVoice;
        row.voicePitch = finalRowPitch;
        row.voiceSpeed = finalRowSpeed;

        try {
          const result = await handleSpeak({
            text: phrase,
            learnLanguage: learnLang,
            voiceName: finalRowVoice,
            mute: true,
            callback: () => {},
            textContainer: null,
            languageCode:
              row.voiceLanguageCode ||
              getVoiceLanguageCode(rowVoice, languages, learnLang),
            isHoverText: false,
            pitch: finalRowPitch,
            voiceSpeed: finalRowSpeed,
          });

          const audioUrl = result ? result.audioUrl : null;
          if (!audioUrl) continue;

          const response = await fetch(audioUrl);
          if (!response.ok) {
            openSnackbar(
              `Failed to fetch audio for phrase: "${phrase}"`,
              "error",
            );
            continue;
          }
          const blob = await response.blob();
          let fileNameWithExtension = fileName.endsWith(".mp3")
            ? fileName
            : `${fileName}.mp3`;
          const contentType = blob.type;

          const allowedAudioTypes = [
            "audio/mpeg",
            "audio/wav",
            "audio/ogg",
            "application/octet-stream",
          ];
          if (!allowedAudioTypes.includes(contentType)) {
            openSnackbar(
              `Unsupported audio type for phrase: "${phrase}"`,
              "error",
            );
            continue;
          }
          if (generateTxt) {
            const fileNameWithoutExtension = row.fileName.replace(".mp3", "");
            const txtContentWithoutQuotes = `${phrase}\n`.replace(/"/g, "");
            folder.file(
              `${fileNameWithoutExtension}.txt`,
              txtContentWithoutQuotes,
            );
          }
          if (fileNameCount[fileNameWithExtension]) {
            fileNameCount[fileNameWithExtension] += 1;
            const baseName = fileNameWithExtension.replace(".mp3", "");
            fileNameWithExtension = `${baseName}_${fileNameCount[fileNameWithExtension]}.mp3`;
          } else {
            fileNameCount[fileNameWithExtension] = 1;
          }

          folder.file(fileNameWithExtension, blob);
        } catch (error) {
          console.error(
            `Error generating audio for ${fileName}: ${error.message}`,
          );
        }
      }

      // Prepare CSV data with updated headers and rows
      const csvContent = [
        newHeaders.join(","),
        ...newData.map((row) =>
          newHeaders.map((header) => row[header]).join(","),
        ),
      ].join("\n");

      // Add CSV file to zip
      zip.file(`updated_data_${timestamp}.csv`, csvContent);

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `audio_files_${timestamp}.zip`);
    } catch (error) {
      console.error(`Error generating audio files: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          style={{ textAlign: "center", marginTop: "15px" }}
          size={{
            xs: 6,
            md: 4,
          }}
        >
          <img
            src={AudioNuala}
            alt='Audio Nuala'
            style={{ maxWidth: "300px" }}
          />
        </Grid>
        <Grid
          style={{ textAlign: "left" }}
          size={{
            xs: 6,
            md: 4,
          }}
        >
          <Typography variant='h5' sx={{
            fontWeight: "bold"
          }}>
            {t("text_studio")}
          </Typography>
          <Typography variant='body1' gutterBottom>
            {t("text_studio_description")}
          </Typography>
        </Grid>
        <Grid
          style={{ textAlign: "center" }}
          size={{
            xs: 12,
            md: 8,
          }}
          sx={{
            marginTop: 2,
            marginBottom: 1
          }}>
          <Divider sx={{ borderBottomWidth: 4 }} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 8,
          }}
        >
          <Grid container sx={{ width: "100%" }}>
            <Grid
              size={{
                xs: 12,
                md: 12,
              }}
              sx={{
                marginTop: 2,
                marginBottom: 1.5
              }}>
              <ToggleButtonGroup
                color='primary'
                value={showCSV}
                exclusive
                onChange={handleChange}
                aria-label='Platform'
                sx={{ marginLeft: "auto", height: "30px" }}
              >
                <ToggleButton value={false}>{t("single_phrase")}</ToggleButton>
                <ToggleButton value={true}>{t("list_of_phrases")}</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 12,
              }}
            >
              {!showCSV && (
                <LanguageSelector
                  id='learnLang'
                  name='learnLang'
                  data-cy='learnLang'
                  label={t("language")}
                  margin='normal'
                  variant='outlined'
                  fullWidth
                  required
                  t={t}
                  languages={languages}
                  value={learnLang}
                  onChange={handleLanguageChange}
                  onBlur={formik.handleBlur}
                />
              )}
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 12,
              }}
            >
              {!showCSV && (
                <ActorForm
                  t={t}
                  actor={formik.values.actor}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched}
                  errors={formik.errors}
                  learnLang={learnLang}
                  filteredVoices={filteredVoices}
                  handleSpeak={handleSpeak}
                  avatars={avatars}
                  langChar={langChar}
                  setFieldValue={formik.setFieldValue}
                  setCheckAudioUrl={setCheckAudioUrl}
                  voiceSpeed={voiceSpeed}
                  setVoiceSpeed={setVoiceSpeed}
                  handleSpeedChange={handleSpeedChange}
                  formik={formik}
                />
              )}
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 12,
              }}
              sx={{
                marginBottom: 2
              }}
            >
              {!showCSV ? (
                <InputHelper
                  t={t}
                  id='phraseToSpeak'
                  data-cy='phraseToSpeak'
                  name='phraseToSpeak'
                  label={t("phrase_to_speak")}
                  value={formik.values.phraseToSpeak}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  type='text'
                  margin='normal'
                  variant='outlined'
                  fullWidth
                  required
                  multiline
                  characters={langChar}
                  learnLang={learnLang}
                  helperText={
                    formik.touched.phraseToSpeak && formik.errors.phraseToSpeak
                  }
                  error={
                    formik.touched.phraseToSpeak &&
                    Boolean(formik.errors.phraseToSpeak)
                  }
                />
              ) : (
                <Grid
                  container
                  spacing={1}
                  size={{
                    xs: 12,
                    md: 12,
                  }}
                  sx={{
                    marginTop: 0.5,
                    marginBottom: 1,
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center"
                  }}>
                  <Grid>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setDialogOpen(true)}
                      variant='contained'
                      sx={{ marginBottom: 2 }}
                    >
                      {t("add_phrase")}
                    </Button>
                  </Grid>
                  <Grid>
                    <label htmlFor='upload-csv'>
                      <Button
                        variant='contained'
                        component='span'
                        startIcon={<FileUploadIcon />}
                        sx={{ marginBottom: 2 }}
                      >
                        {t("upload_csv")}
                      </Button>
                    </label>
                    <input
                      id='upload-csv'
                      type='file'
                      accept='.csv'
                      onChange={handleCSVUpload}
                      style={{ display: "none" }}
                    />
                  </Grid>
                  <Grid>
                    <Button
                      variant='outlined'
                      startIcon={<DownloadIcon />}
                      onClick={downloadCSVTemplate}
                      sx={{ marginBottom: 2 }}
                    >
                      {t("download_csv_template")}
                    </Button>
                  </Grid>
                  <Grid sx={{ justifyContent: "left" }} size={12}>
                    {csvData.length === 0 && (
                      <Typography variant='body1' sx={{ marginTop: 1 }}>
                        {t("no_phrases_yet")}
                      </Typography>
                    )}
                  </Grid>
                  <Grid
                    container
                    spacing={1}
                    size={{
                      xs: 12,
                      md: 12,
                    }}
                    sx={{
                      marginBottom: 1
                    }}
                  >
                    {csvData.length > 0 && (
                      <>
                        <Grid size={12}>
                          <TableContainer component={Paper}>
                            <Table
                              sx={{ minWidth: 650 }}
                              aria-label='csv table'
                            >
                              <TableHead>
                                <TableRow>
                                  {csvHeaders.map((header, index) => (
                                    <TableCell key={index} align='center'>
                                      <strong>{header}</strong>
                                    </TableCell>
                                  ))}
                                  <TableCell align='center' width='50px'>
                                    <strong>{t("play")}</strong>
                                  </TableCell>
                                  <TableCell align='center' width='50px'>
                                    <strong>{t("edit")}</strong>{" "}
                                  </TableCell>
                                  <TableCell align='center' width='50px'>
                                    <strong>{t("delete")}</strong>{" "}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {csvData.map((row, rowIndex) => (
                                  <TableRow key={rowIndex}>
                                    {csvHeaders.map((header, colIndex) => (
                                      <TableCell
                                        key={colIndex}
                                        align='center'
                                        sx={{
                                          whiteSpace: "normal",
                                          overflowWrap: "break-word",
                                          wordBreak:
                                            colIndex === 1
                                              ? "break-all"
                                              : "normal",
                                          maxWidth:
                                            colIndex === 0 ? "300px" : "200px",
                                        }}
                                      >
                                        {row[header] || null}
                                      </TableCell>
                                    ))}
                                    <TableCell align='center'>
                                      <IconButton
                                        onClick={() =>
                                          playPhraseAudio(
                                            row.phrase,
                                            row.voice,
                                            row.voiceLanguageCode,
                                          )
                                        }
                                        aria-label='play'
                                      >
                                        <PlayArrowIcon />
                                      </IconButton>
                                    </TableCell>
                                    <TableCell align='center'>
                                      <IconButton
                                        onClick={() =>
                                          openEditDialog(row, rowIndex)
                                        }
                                        aria-label='edit'
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </TableCell>
                                    <TableCell align='center'>
                                      <IconButton
                                        onClick={() =>
                                          handleDeleteRow(rowIndex)
                                        } // Call the row deletion function
                                        aria-label='delete'
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  {csvData.length > 0 && (
                    <Grid
                      sx={{
                        display: "flex",
                        justifyContent: "left",
                        alignItems: "center",
                        marginTop: 1,
                      }}
                      size={12}
                    >
                      <LoadingButton
                        variant='contained'
                        onClick={generateAudioForCSV}
                        loading={isGenerating}
                        startIcon={<DownloadIcon />}
                        sx={{ marginBottom: 2 }}
                      >
                        {t("download_audio")}
                      </LoadingButton>
                      <Checkbox
                        checked={generateTxt}
                        onChange={() => setGenerateTxt(!generateTxt)}
                        sx={{ marginBottom: 2 }}
                      />
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          marginBottom: 2.5
                        }}>
                        {t("generate_transcript")}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
              <AddPhraseDialog
                t={t}
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                handleAddPhrase={handleAddPhrase}
                initialValues={{
                  learnLang: "english",
                  actor: {
                    voice: "",
                    voiceLanguageCode: "",
                    voicePitch: "",
                    picture: "",
                  },
                  phraseToSpeak: "",
                  voiceSpeed: 50,
                }}
                languages={languages}
                avatars={avatars}
                handleSpeak={handleSpeak}
              />
              <EditPhraseDialog
                t={t}
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                handleUpdatePhrase={handleUpdatePhrase}
                languages={languages}
                learnLang={learnLang}
                avatars={avatars}
                handleSpeak={handleSpeak}
                langChar={langChar}
                selectedRow={selectedRow}
                rowIndex={rowIndex}
              />
            </Grid>
            {!showCSV && (
              <Grid
                container
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                size={{
                  xs: 12,
                  md: 12,
                }}
              >
                <Button
                  color='primary'
                  component='span'
                  type='submit'
                  variant='outlined'
                  onClick={() =>
                    handleSpeak(
                      {
                        text: formik.values.phraseToSpeak,
                        learnLanguage: learnLang,
                        voiceName: formik.values.actor.voice,
                        mute: false,
                        callback: () => {},
                        textContainer: null,
                        languageCode: formik.values.actor.voiceLanguageCode,
                        isHoverText: false,
                        pitch: formik.values.actor.voicePitch,
                        voiceSpeed: formik.values.voiceSpeed,
                      },
                      setCheckAudioUrl(false),
                    )
                  }
                  sx={{
                    marginRight: 2,
                    width: "150px",
                    marginBottom: 2,
                  }}
                  disabled={
                    formik.values.phraseToSpeak === "" ||
                    !formik.values.actor.voice
                  }
                >
                  {t("play_audio")}
                </Button>
                <Button
                  color='primary'
                  component='span'
                  variant='contained'
                  onClick={() =>
                    downloadAudio({
                      language: learnLang,
                      voiceName: formik.values.actor.voice,
                      text: formik.values.phraseToSpeak,
                    })
                  }
                  sx={{
                    width: "150px",
                    marginBottom: 2,
                  }}
                  disabled={
                    !audioUrl ||
                    formik.values.phraseToSpeak === "" ||
                    checkAudioUrl
                  }
                >
                  {t("download")}
                </Button>
                <Checkbox
                  checked={generateTxt}
                  onChange={() => setGenerateTxt(!generateTxt)}
                  sx={{ marginBottom: 2 }}
                />
                <Typography
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 2.5
                  }}>
                  {t("generate_transcript")}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
}

export default GenerateAudio;
